import { SyntheticEvent, useState } from "react";
import { Button, Modal, Form, Alert } from "react-bootstrap";
import { TemplateTextModel, templateTextApi } from "../../api/TemplateTextApi";
import { TemplateTextTypeModel } from "../../api/TemplateTextTypeApi";
import { ValidationProblem, formErrors } from "../../api/backendApi";

interface TemplateTextModalProps {
  templateText: TemplateTextModel;
  isCreate: boolean;
  templateTextTypes: TemplateTextTypeModel[];
  onChange: (templateTextModel: TemplateTextModel) => void;
  onCancel: () => void;
  modalText: string;
}

function TemplateTextModal({
  templateText: initialTemplateText,
  isCreate,
  templateTextTypes,
  onChange,
  onCancel,
  modalText,
}: TemplateTextModalProps) {
  const [templateText, setTemplateText] = useState<TemplateTextModel>({
    ...initialTemplateText,
    crsInfo: initialTemplateText.crsInfo?.replaceAll("<NL>", "\r\n"),
  });
  const [show, setShow] = useState(true);
  const [errors, setErrors] = useState<formErrors>({});
  const [showRawString, setShowRawString] = useState(false);

  const handleClose = () => {
    setShow(false);
    onCancel();
    setTemplateText(initialTemplateText);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    const normalizedTemplateText = {
      ...templateText,
      crsInfo: templateText.crsInfo?.replaceAll(/(?:\r\n|\r|\n)/g, "<NL>"),
    };

    let call = isCreate
      ? templateTextApi.createTemplateText(normalizedTemplateText)
      : templateTextApi.updateTemplateText(
          normalizedTemplateText,
          initialTemplateText.dxfBlockName,
          initialTemplateText.defaultTemplateTextTypeId === undefined
            ? 0
            : initialTemplateText.defaultTemplateTextTypeId,
        );

    call.then((result) => {
      if (result.ok) {
        onChange(normalizedTemplateText);
        handleClose();
      } else {
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    });
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    var change = null;

    if (name === "defaultTemplateTextTypeId") {
      const templateTextTypeId = parseInt(value);
      change = { [name]: templateTextTypeId };
    } else {
      change = { [name]: value };
    }

    const modified: TemplateTextModel = { ...templateText, ...change };

    setTemplateText(modified);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalText}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit} id="templateTextForm">
            {errors[""] && (
              <div style={{ marginTop: "1rem" }}>
                {errors[""].map((errorMessage, index) => (
                  <Alert key={errorMessage} variant="danger">
                    <p key={index}>{errorMessage}</p>
                  </Alert>
                ))}
              </div>
            )}
            <Form.Group>
              <Form.Label>Block name</Form.Label>
              <Form.Control
                name="dxfBlockName"
                type="text"
                value={templateText.dxfBlockName ?? ""}
                onChange={handleChange}
              />
              {errors.dxfBlockName && (
                <div style={{ marginTop: "1rem" }}>
                  {errors.dxfBlockName.map((e, index) => (
                    <Alert key={e} variant="danger">
                      <p>{e}</p>
                    </Alert>
                  ))}
                </div>
              )}
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="exampleForm.ControlTextarea1"
            >
              <Form.Label>Crs name</Form.Label>
              <Form.Control
                name="crsName"
                type="text"
                value={templateText.crsName ?? ""}
                onChange={handleChange}
              />
              {errors.crsName && (
                <div style={{ marginTop: "1rem" }}>
                  {errors.crsName.map((e, index) => (
                    <Alert key={e} variant="danger">
                      <p>{e}</p>
                    </Alert>
                  ))}
                </div>
              )}
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="exampleForm.ControlTextarea1"
            >
              <Form.Check
                type="switch"
                label="View with <NL> tags"
                checked={showRawString}
                onChange={(e) => setShowRawString(e.currentTarget.checked)}
                style={{ float: "right" }}
              />
              <Form.Label>Crs info</Form.Label>
              {showRawString ? (
                <Form.Control
                  name="crsInfo"
                  as="textarea"
                  rows={!templateText.crsInfo ? 1 : 10}
                  value={
                    templateText.crsInfo?.replaceAll(
                      /(?:\r\n|\r|\n)/g,
                      "<NL>",
                    ) ?? ""
                  }
                  disabled={true}
                />
              ) : (
                <Form.Control
                  name="crsInfo"
                  as="textarea"
                  rows={!templateText.crsInfo ? 1 : 10}
                  value={templateText.crsInfo ?? ""}
                  onChange={handleChange}
                  style={{ whiteSpace: "pre" }}
                />
              )}
            </Form.Group>
            <Form.Group
              className="mb-3"
              controlId="exampleForm.ControlTextarea1"
            >
              <Form.Label>template text type</Form.Label>
              <Form.Select
                name="defaultTemplateTextTypeId"
                value={templateText.defaultTemplateTextTypeId}
                onChange={handleChange}
              >
                {templateTextTypes.map((defaultTemplateTextType) => (
                  <option
                    key={defaultTemplateTextType.id}
                    value={defaultTemplateTextType.id}
                  >
                    {defaultTemplateTextType.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" form="templateTextForm" type="submit">
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TemplateTextModal;
